import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import {
    Globe,
    Copy,
    Check,
    Eye,
    EyeOff,
    Loader2,
    Sparkles,
    Wand2,
    Palette,
    Layout,
    Type,
    Link as LinkIcon,
    Instagram,
    Phone,
    Globe2,
    Plus,
    Trash2,
    Save,
    Crown,
    ArrowUpRight,
    MessageCircle,
    Mail,
    MapPin,
    Award,
    X,
} from 'lucide-react';

// Bio Template Types
interface BioTemplate {
    id: string;
    name: string;
    preview: string;
    primaryColor: string;
    secondaryColor: string;
    style: 'minimal' | 'gradient' | 'dark' | 'light' | 'bold';
}

interface BioButton {
    id: string;
    label: string;
    url: string;
    icon: 'whatsapp' | 'instagram' | 'website' | 'email' | 'phone' | 'custom';
    order: number;
}

interface BioData {
    display_name: string;
    headline: string;
    bio: string;
    photo_url: string;
    specialties: string[];
    certifications: string[];
    location: string;
    experience_years: number;
    whatsapp_number: string;
    instagram_handle: string;
    website_url: string;
    buttons: BioButton[];
    template_id: string;
    primary_color: string;
    secondary_color: string;
}

// Available Templates
const TEMPLATES: BioTemplate[] = [
    {
        id: 'minimal',
        name: 'Minimalista',
        preview: 'bg-white text-gray-900',
        primaryColor: '#6366f1',
        secondaryColor: '#a855f7',
        style: 'minimal',
    },
    {
        id: 'gradient',
        name: 'Gradiente Premium',
        preview: 'bg-gradient-to-br from-purple-600 to-pink-500 text-white',
        primaryColor: '#9333ea',
        secondaryColor: '#ec4899',
        style: 'gradient',
    },
    {
        id: 'dark',
        name: 'Dark Profissional',
        preview: 'bg-gray-900 text-white',
        primaryColor: '#10b981',
        secondaryColor: '#14b8a6',
        style: 'dark',
    },
    {
        id: 'bold',
        name: 'Bold Energia',
        preview: 'bg-orange-500 text-white',
        primaryColor: '#f97316',
        secondaryColor: '#eab308',
        style: 'bold',
    },
];

// AI Text Generation Limits
const TEXT_LIMITS: Record<string, { max: number; description: string }> = {
    headline: { max: 60, description: 'Uma frase curta e impactante sobre você' },
    bio: { max: 300, description: 'Sua bio completa descrevendo sua experiência' },
    button_label: { max: 25, description: 'Texto curto para o botão' },
    specialty: { max: 30, description: 'Uma especialidade específica' },
    certification: { max: 50, description: 'Nome da certificação' },
};

export function BioPage() {
    const { profile, tenant, user } = useAuth();
    const queryClient = useQueryClient();
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState<'content' | 'template' | 'colors'>('content');
    const [showPreview, setShowPreview] = useState(false);
    const [showAIModal, setShowAIModal] = useState(false);
    const [aiField, setAiField] = useState<string>('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [aiResult, setAiResult] = useState('');

    // Premium check (replace with actual subscription check)
    const isPremium = true; // TODO: Check actual subscription status

    // Form state
    const [bioData, setBioData] = useState<BioData>({
        display_name: '',
        headline: '',
        bio: '',
        photo_url: '',
        specialties: [],
        certifications: [],
        location: '',
        experience_years: 0,
        whatsapp_number: '',
        instagram_handle: '',
        website_url: '',
        buttons: [],
        template_id: 'minimal',
        primary_color: '#6366f1',
        secondary_color: '#a855f7',
    });

    const [bioEnabled, setBioEnabled] = useState(false);
    const [slugInput, setSlugInput] = useState('');
    const [newSpecialty, setNewSpecialty] = useState('');
    const [newCertification, setNewCertification] = useState('');

    // Load existing profile
    const { data: existingProfile, isLoading } = useQuery({
        queryKey: ['bio-profile', profile?.id],
        queryFn: async () => {
            if (!profile?.id) return null;
            const { data, error } = await supabase
                .from('tinderfit_profiles')
                .select('*')
                .eq('user_id', profile.id)
                .maybeSingle();
            if (error) throw error;
            return data;
        },
        enabled: !!profile?.id,
    });

    // Load tenant settings
    const { data: tenantData } = useQuery({
        queryKey: ['tenant-bio-settings', tenant?.id],
        queryFn: async () => {
            if (!tenant?.id) return null;
            const { data, error } = await supabase
                .from('tenants')
                .select('bio_page_slug, bio_page_enabled')
                .eq('id', tenant.id)
                .single();
            if (error) throw error;
            return data;
        },
        enabled: !!tenant?.id,
    });

    // Initialize form data
    useEffect(() => {
        if (existingProfile) {
            setBioData({
                display_name: existingProfile.display_name || profile?.full_name || '',
                headline: existingProfile.headline || '',
                bio: existingProfile.bio || '',
                photo_url: existingProfile.photo_url || '',
                specialties: existingProfile.specialties || [],
                certifications: existingProfile.certifications || [],
                location: existingProfile.preferred_gym || '',
                experience_years: existingProfile.experience_years || 0,
                whatsapp_number: existingProfile.whatsapp_number || '',
                instagram_handle: existingProfile.instagram_handle || '',
                website_url: existingProfile.website_url || '',
                buttons: existingProfile.buttons || [],
                template_id: existingProfile.template_id || 'minimal',
                primary_color: existingProfile.primary_color || '#6366f1',
                secondary_color: existingProfile.secondary_color || '#a855f7',
            });
        }
        if (tenantData) {
            setSlugInput(tenantData.bio_page_slug || '');
            setBioEnabled(tenantData.bio_page_enabled || false);
        }
    }, [existingProfile, tenantData, profile]);

    // Save mutation
    const saveMutation = useMutation({
        mutationFn: async () => {
            if (!profile?.id || !tenant?.id) throw new Error('Not authenticated');

            // Save profile
            const profileData = {
                user_id: profile.id,
                display_name: bioData.display_name,
                headline: bioData.headline,
                bio: bioData.bio,
                photo_url: bioData.photo_url,
                specialties: bioData.specialties,
                certifications: bioData.certifications,
                preferred_gym: bioData.location,
                experience_years: bioData.experience_years,
                whatsapp_number: bioData.whatsapp_number,
                instagram_handle: bioData.instagram_handle,
                website_url: bioData.website_url,
                buttons: bioData.buttons,
                template_id: bioData.template_id,
                primary_color: bioData.primary_color,
                secondary_color: bioData.secondary_color,
                is_visible: bioEnabled,
            };

            const { error: profileError } = await supabase
                .from('tinderfit_profiles')
                .upsert(profileData, { onConflict: 'user_id' });
            if (profileError) throw profileError;

            // Save tenant settings
            const { error: tenantError } = await supabase
                .from('tenants')
                .update({
                    bio_page_slug: slugInput,
                    bio_page_enabled: bioEnabled,
                })
                .eq('id', tenant.id);
            if (tenantError) throw tenantError;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bio-profile'] });
            queryClient.invalidateQueries({ queryKey: ['tenant-bio-settings'] });
        },
    });

    // AI Text Generation
    const generateWithAI = async () => {
        if (!aiPrompt.trim() || !aiField) return;
        setAiLoading(true);

        try {
            const limit = TEXT_LIMITS[aiField]?.max || 100;

            // Call Edge Function for AI generation
            const { data, error } = await supabase.functions.invoke('generate-bio-text', {
                body: {
                    prompt: aiPrompt,
                    field: aiField,
                    maxLength: limit,
                    context: {
                        name: bioData.display_name,
                        specialties: bioData.specialties,
                        experience: bioData.experience_years,
                    },
                },
            });

            if (error) throw error;
            setAiResult(data?.text || 'Erro ao gerar texto');
        } catch (err) {
            console.error('AI generation error:', err);
            // Fallback: generate simple text locally
            const fallbackTexts: Record<string, string[]> = {
                headline: [
                    `Personal Trainer especializado em transformar vidas`,
                    `Treinamento personalizado para seus objetivos`,
                    `Seu parceiro na jornada fitness`,
                ],
                bio: [
                    `Sou Personal Trainer com ${bioData.experience_years || 5}+ anos de experiência, apaixonado por ajudar pessoas a alcançarem seus objetivos. Minha metodologia combina treino inteligente, acompanhamento próximo e muita dedicação.`,
                ],
                button_label: ['Fale Comigo', 'Agende uma Aula', 'Saiba Mais', 'Me Siga'],
            };
            const options = fallbackTexts[aiField] || ['Texto gerado'];
            setAiResult(options[Math.floor(Math.random() * options.length)]);
        } finally {
            setAiLoading(false);
        }
    };

    const applyAIResult = () => {
        if (!aiResult || !aiField) return;

        switch (aiField) {
            case 'headline':
                setBioData({ ...bioData, headline: aiResult });
                break;
            case 'bio':
                setBioData({ ...bioData, bio: aiResult });
                break;
            case 'specialty':
                setBioData({ ...bioData, specialties: [...bioData.specialties, aiResult] });
                break;
            case 'certification':
                setBioData({ ...bioData, certifications: [...bioData.certifications, aiResult] });
                break;
        }
        setShowAIModal(false);
        setAiPrompt('');
        setAiResult('');
    };

    const openAIModal = (field: string) => {
        setAiField(field);
        setAiPrompt('');
        setAiResult('');
        setShowAIModal(true);
    };

    const copyLink = () => {
        const url = `${window.location.origin}/bio/${slugInput}`;
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const addButton = () => {
        const newButton: BioButton = {
            id: Date.now().toString(),
            label: 'Novo Botão',
            url: '',
            icon: 'custom',
            order: bioData.buttons.length,
        };
        setBioData({ ...bioData, buttons: [...bioData.buttons, newButton] });
    };

    const updateButton = (id: string, updates: Partial<BioButton>) => {
        setBioData({
            ...bioData,
            buttons: bioData.buttons.map(btn =>
                btn.id === id ? { ...btn, ...updates } : btn
            ),
        });
    };

    const removeButton = (id: string) => {
        setBioData({
            ...bioData,
            buttons: bioData.buttons.filter(btn => btn.id !== id),
        });
    };

    if (!isPremium) {
        return (
            <div className="max-w-2xl mx-auto text-center py-16 px-4">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-6">
                    <Crown className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Recurso Premium
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    A Bio Page é um recurso exclusivo para assinantes PRO.
                    Faça upgrade para criar sua página profissional.
                </p>
                <a href="/billing" className="btn-primary inline-flex items-center gap-2">
                    <Crown className="w-4 h-4" />
                    Fazer Upgrade
                </a>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const selectedTemplate = TEMPLATES.find(t => t.id === bioData.template_id) || TEMPLATES[0];

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Globe className="w-6 h-6" />
                        Minha Bio Page
                        <span className="px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full">
                            PRO
                        </span>
                    </h1>
                    <p className="text-gray-500">Sua página profissional para captar novos alunos</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowPreview(!showPreview)}
                        className="btn-secondary"
                    >
                        {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                        {showPreview ? 'Editar' : 'Visualizar'}
                    </button>
                    <button
                        onClick={() => saveMutation.mutate()}
                        disabled={saveMutation.isPending}
                        className="btn-primary"
                    >
                        {saveMutation.isPending ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Salvar
                    </button>
                </div>
            </div>

            {/* URL Configuration */}
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">URL da sua página</h2>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-sm text-gray-500">
                            {bioEnabled ? 'Ativa' : 'Inativa'}
                        </span>
                        <div
                            onClick={() => setBioEnabled(!bioEnabled)}
                            className={`w-12 h-6 rounded-full transition-colors cursor-pointer ${bioEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                        >
                            <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform mt-0.5 ${bioEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                        </div>
                    </label>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                        <span className="px-3 py-2 text-sm text-gray-500 bg-gray-200 dark:bg-gray-700">
                            fitflow.com/bio/
                        </span>
                        <input
                            type="text"
                            value={slugInput}
                            onChange={(e) => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                            placeholder="seu-nome"
                            className="flex-1 px-3 py-2 bg-transparent border-0 focus:ring-0"
                        />
                    </div>
                    <button onClick={copyLink} className="btn-secondary">
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                    {bioEnabled && slugInput && (
                        <a
                            href={`/bio/${slugInput}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary"
                        >
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
                {[
                    { id: 'content', label: 'Conteúdo', icon: Type },
                    { id: 'template', label: 'Template', icon: Layout },
                    { id: 'colors', label: 'Cores', icon: Palette },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${activeTab === tab.id
                            ? 'border-primary-600 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content Tab */}
            {activeTab === 'content' && (
                <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="card">
                        <h3 className="font-semibold mb-4">Informações Básicas</h3>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="label">Nome de Exibição</label>
                                <input
                                    type="text"
                                    value={bioData.display_name}
                                    onChange={(e) => setBioData({ ...bioData, display_name: e.target.value })}
                                    className="input"
                                    placeholder="Seu nome profissional"
                                />
                            </div>
                            <div>
                                <label className="label">Anos de Experiência</label>
                                <input
                                    type="number"
                                    value={bioData.experience_years}
                                    onChange={(e) => setBioData({ ...bioData, experience_years: parseInt(e.target.value) || 0 })}
                                    className="input"
                                    min="0"
                                />
                            </div>
                        </div>

                        <div className="mt-4">
                            <label className="label flex items-center justify-between">
                                Headline
                                <button
                                    onClick={() => openAIModal('headline')}
                                    className="text-primary-600 hover:text-primary-700 flex items-center gap-1 text-sm"
                                >
                                    <Wand2 className="w-4 h-4" />
                                    Gerar com IA
                                </button>
                            </label>
                            <input
                                type="text"
                                value={bioData.headline}
                                onChange={(e) => setBioData({ ...bioData, headline: e.target.value })}
                                className="input"
                                placeholder="Ex: Personal Trainer especializado em Emagrecimento"
                                maxLength={60}
                            />
                            <p className="text-xs text-gray-400 mt-1">{bioData.headline.length}/60 caracteres</p>
                        </div>

                        <div className="mt-4">
                            <label className="label flex items-center justify-between">
                                Bio
                                <button
                                    onClick={() => openAIModal('bio')}
                                    className="text-primary-600 hover:text-primary-700 flex items-center gap-1 text-sm"
                                >
                                    <Wand2 className="w-4 h-4" />
                                    Gerar com IA
                                </button>
                            </label>
                            <textarea
                                value={bioData.bio}
                                onChange={(e) => setBioData({ ...bioData, bio: e.target.value })}
                                className="input h-24 resize-none"
                                placeholder="Conte um pouco sobre você, sua metodologia e experiência..."
                                maxLength={300}
                            />
                            <p className="text-xs text-gray-400 mt-1">{bioData.bio.length}/300 caracteres</p>
                        </div>

                        <div className="mt-4">
                            <label className="label flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                Localização / Academia
                            </label>
                            <input
                                type="text"
                                value={bioData.location}
                                onChange={(e) => setBioData({ ...bioData, location: e.target.value })}
                                className="input"
                                placeholder="Ex: Smart Fit - Copacabana, Rio de Janeiro"
                            />
                        </div>
                    </div>

                    {/* Contact Info */}
                    <div className="card">
                        <h3 className="font-semibold mb-4">Contato e Redes</h3>
                        <div className="grid gap-4 md:grid-cols-3">
                            <div>
                                <label className="label flex items-center gap-2">
                                    <MessageCircle className="w-4 h-4" />
                                    WhatsApp
                                </label>
                                <input
                                    type="text"
                                    value={bioData.whatsapp_number}
                                    onChange={(e) => setBioData({ ...bioData, whatsapp_number: e.target.value })}
                                    className="input"
                                    placeholder="5521999999999"
                                />
                            </div>
                            <div>
                                <label className="label flex items-center gap-2">
                                    <Instagram className="w-4 h-4" />
                                    Instagram
                                </label>
                                <input
                                    type="text"
                                    value={bioData.instagram_handle}
                                    onChange={(e) => setBioData({ ...bioData, instagram_handle: e.target.value })}
                                    className="input"
                                    placeholder="@seuinstagram"
                                />
                            </div>
                            <div>
                                <label className="label flex items-center gap-2">
                                    <Globe2 className="w-4 h-4" />
                                    Website
                                </label>
                                <input
                                    type="text"
                                    value={bioData.website_url}
                                    onChange={(e) => setBioData({ ...bioData, website_url: e.target.value })}
                                    className="input"
                                    placeholder="https://seusite.com"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Specialties */}
                    <div className="card">
                        <h3 className="font-semibold mb-4 flex items-center justify-between">
                            Especialidades
                            <button
                                onClick={() => openAIModal('specialty')}
                                className="text-primary-600 hover:text-primary-700 flex items-center gap-1 text-sm"
                            >
                                <Wand2 className="w-4 h-4" />
                                Sugerir
                            </button>
                        </h3>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {bioData.specialties.map((spec, i) => (
                                <span key={i} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm flex items-center gap-1">
                                    {spec}
                                    <button onClick={() => setBioData({
                                        ...bioData,
                                        specialties: bioData.specialties.filter((_, idx) => idx !== i)
                                    })}>
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newSpecialty}
                                onChange={(e) => setNewSpecialty(e.target.value)}
                                className="input flex-1"
                                placeholder="Ex: Musculação, Emagrecimento..."
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && newSpecialty.trim()) {
                                        setBioData({ ...bioData, specialties: [...bioData.specialties, newSpecialty.trim()] });
                                        setNewSpecialty('');
                                    }
                                }}
                            />
                            <button
                                onClick={() => {
                                    if (newSpecialty.trim()) {
                                        setBioData({ ...bioData, specialties: [...bioData.specialties, newSpecialty.trim()] });
                                        setNewSpecialty('');
                                    }
                                }}
                                className="btn-secondary"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Certifications */}
                    <div className="card">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Award className="w-5 h-5" />
                            Certificações
                        </h3>
                        <div className="space-y-2 mb-3">
                            {bioData.certifications.map((cert, i) => (
                                <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                    <Award className="w-4 h-4 text-yellow-500" />
                                    <span className="flex-1">{cert}</span>
                                    <button onClick={() => setBioData({
                                        ...bioData,
                                        certifications: bioData.certifications.filter((_, idx) => idx !== i)
                                    })} className="p-1 hover:bg-red-100 rounded text-red-500">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newCertification}
                                onChange={(e) => setNewCertification(e.target.value)}
                                className="input flex-1"
                                placeholder="Ex: CREF 123456-G/SP"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && newCertification.trim()) {
                                        setBioData({ ...bioData, certifications: [...bioData.certifications, newCertification.trim()] });
                                        setNewCertification('');
                                    }
                                }}
                            />
                            <button
                                onClick={() => {
                                    if (newCertification.trim()) {
                                        setBioData({ ...bioData, certifications: [...bioData.certifications, newCertification.trim()] });
                                        setNewCertification('');
                                    }
                                }}
                                className="btn-secondary"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Custom Buttons */}
                    <div className="card">
                        <h3 className="font-semibold mb-4 flex items-center justify-between">
                            Botões Personalizados
                            <button onClick={addButton} className="btn-secondary text-sm">
                                <Plus className="w-4 h-4 mr-1" />
                                Adicionar
                            </button>
                        </h3>
                        <div className="space-y-3">
                            {bioData.buttons.map((btn) => (
                                <div key={btn.id} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                                    <div className="grid gap-3 md:grid-cols-3">
                                        <input
                                            type="text"
                                            value={btn.label}
                                            onChange={(e) => updateButton(btn.id, { label: e.target.value })}
                                            className="input"
                                            placeholder="Texto do botão"
                                            maxLength={25}
                                        />
                                        <input
                                            type="text"
                                            value={btn.url}
                                            onChange={(e) => updateButton(btn.id, { url: e.target.value })}
                                            className="input"
                                            placeholder="URL do link"
                                        />
                                        <div className="flex gap-2">
                                            <select
                                                value={btn.icon}
                                                onChange={(e) => updateButton(btn.id, { icon: e.target.value as any })}
                                                className="input flex-1"
                                            >
                                                <option value="custom">Link</option>
                                                <option value="whatsapp">WhatsApp</option>
                                                <option value="instagram">Instagram</option>
                                                <option value="email">Email</option>
                                                <option value="phone">Telefone</option>
                                            </select>
                                            <button
                                                onClick={() => removeButton(btn.id)}
                                                className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {bioData.buttons.length === 0 && (
                                <p className="text-center text-gray-400 py-4">
                                    Adicione botões personalizados para sua bio
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Template Tab */}
            {activeTab === 'template' && (
                <div className="card">
                    <h3 className="font-semibold mb-4">Escolha um Template</h3>
                    <div className="grid gap-4 md:grid-cols-2">
                        {TEMPLATES.map((template) => (
                            <button
                                key={template.id}
                                onClick={() => setBioData({
                                    ...bioData,
                                    template_id: template.id,
                                    primary_color: template.primaryColor,
                                    secondary_color: template.secondaryColor,
                                })}
                                className={`p-4 rounded-xl border-2 transition-all text-left ${bioData.template_id === template.id
                                    ? 'border-primary-500 ring-2 ring-primary-200'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className={`h-24 rounded-lg mb-3 ${template.preview} flex items-center justify-center`}>
                                    <span className="text-2xl font-bold">Aa</span>
                                </div>
                                <p className="font-medium">{template.name}</p>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Colors Tab */}
            {activeTab === 'colors' && (
                <div className="card">
                    <h3 className="font-semibold mb-4">Personalizar Cores</h3>
                    <div className="grid gap-6 md:grid-cols-2">
                        <div>
                            <label className="label">Cor Principal</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="color"
                                    value={bioData.primary_color}
                                    onChange={(e) => setBioData({ ...bioData, primary_color: e.target.value })}
                                    className="w-16 h-16 rounded-lg border-0 cursor-pointer"
                                />
                                <input
                                    type="text"
                                    value={bioData.primary_color}
                                    onChange={(e) => setBioData({ ...bioData, primary_color: e.target.value })}
                                    className="input w-32"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="label">Cor Secundária</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="color"
                                    value={bioData.secondary_color}
                                    onChange={(e) => setBioData({ ...bioData, secondary_color: e.target.value })}
                                    className="w-16 h-16 rounded-lg border-0 cursor-pointer"
                                />
                                <input
                                    type="text"
                                    value={bioData.secondary_color}
                                    onChange={(e) => setBioData({ ...bioData, secondary_color: e.target.value })}
                                    className="input w-32"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 p-4 rounded-xl" style={{ background: `linear-gradient(135deg, ${bioData.primary_color}, ${bioData.secondary_color})` }}>
                        <p className="text-white text-center font-bold">Preview do Gradiente</p>
                    </div>
                </div>
            )}

            {/* AI Modal */}
            {showAIModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-primary-600" />
                                Gerar com IA
                            </h3>
                            <button onClick={() => setShowAIModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="label">O que você quer?</label>
                                <p className="text-sm text-gray-500 mb-2">
                                    {TEXT_LIMITS[aiField]?.description || 'Descreva o que você precisa'}
                                </p>
                                <textarea
                                    value={aiPrompt}
                                    onChange={(e) => setAiPrompt(e.target.value)}
                                    className="input h-24 resize-none"
                                    placeholder={`Ex: ${aiField === 'headline' ? 'Uma frase motivacional sobre transformação física' : 'Descreva o que você quer...'}`}
                                />
                            </div>

                            <button
                                onClick={generateWithAI}
                                disabled={aiLoading || !aiPrompt.trim()}
                                className="btn-primary w-full"
                            >
                                {aiLoading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Wand2 className="w-4 h-4 mr-2" />
                                )}
                                {aiLoading ? 'Gerando...' : 'Gerar Texto'}
                            </button>

                            {aiResult && (
                                <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-200 dark:border-primary-800">
                                    <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">Resultado:</p>
                                    <p className="text-gray-700 dark:text-gray-300">{aiResult}</p>
                                    <div className="flex gap-2 mt-3">
                                        <button onClick={generateWithAI} className="btn-secondary flex-1 text-sm">
                                            Gerar Outro
                                        </button>
                                        <button onClick={applyAIResult} className="btn-primary flex-1 text-sm">
                                            Usar Este
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
